import {
  DependsOnDto,
  FormFieldDto,
  SectionDto,
} from '../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import {
  createFutureOrCurrentDateValidation,
  createPastDateValidation,
} from './DateValidationsUtil'

import { DateValidationFunction } from '../components/common/DateField'
import FormUtils from './FormUtils'
import customerStore from '../datastore/CustomerStore'
import dayjs from 'dayjs'

// Utility function to evaluate dependency logic
export const evaluateDependency = (dependency: DependsOnDto, formData: any) => {
  const { logic_operator, field_value_list } = dependency

  const results = field_value_list.map((condition) => {
    const fieldValue = condition.attribute_name
      ? formData[condition.attribute_name]
      : formData[condition.id]

    if (condition.child_id) {
      // Handle checkbox with specific child_id
      return (
        Array.isArray(fieldValue) && fieldValue.includes(condition.child_id)
      )
    }

    return fieldValue === condition.value
  })

  return logic_operator === 'AND'
    ? results.every((result) => result)
    : results.some((result) => result)
}

// Data transformation utilities
export const transformDataForSubmission = (
  formData: any,
  allSections: SectionDto[]
) => {
  const transformedData: any = {
    desired_underwriting_type: formData.underwriting_type,
  }

  // Get visible sections based on form data
  const visibleSections = getVisibleSections(
    allSections,
    formData,
    getDisabledSections(false)
  )

  // Get all visible fields from all visible sections
  const allVisibleFields = visibleSections.flatMap((section) =>
    getVisibleFields(section, formData)
  )

  // Create a Set of visible field IDs for quick lookup
  const visibleFieldIds = new Set(
    allVisibleFields.flatMap((field) => {
      const fieldIds = [field.id]

      // Add child field IDs if they exist
      if (field.child_fields && Array.isArray(field.child_fields)) {
        fieldIds.push(...field.child_fields.map((childField) => childField.id))
      }

      return fieldIds
    })
  )

  // Add disabled field IDs to the processing set
  const fieldsToProcess = new Set([
    ...Array.from(visibleFieldIds),
    ...getDisabledFields(),
  ])

  // Only process form data for visible fields + disabled fields
  Object.entries(formData).forEach(([key, value]) => {
    // Skip if this field is not in our processing set
    if (!fieldsToProcess.has(key)) {
      return
    }

    // Find the field definition (check visible fields first, then all sections if disabled field)
    let field =
      allVisibleFields.find((f) => f.id === key) ||
      allVisibleFields
        .filter((f) => f.child_fields && Array.isArray(f.child_fields))
        .flatMap((f) => f.child_fields!)
        .find((childField) => childField.id === key)

    // If not found in visible fields and it's a disabled field, search all sections
    if (!field && getDisabledFields().includes(key)) {
      const allFields = allSections.flatMap((section) => section.body)
      field =
        allFields.find((f) => f.id === key) ||
        allFields
          .filter((f) => f.child_fields && Array.isArray(f.child_fields))
          .flatMap((f) => f.child_fields!)
          .find((childField) => childField.id === key)
    }

    if (!field) {
      // For disabled fields without definition, use the key as tag
      transformedData[key] = value
      return
    }

    // Use field tag or fallback to field id
    const fieldTag = field.tag || key

    // Transform data based on field type
    switch (field.type) {
      case 'height_field':
        if (
          value &&
          typeof value === 'object' &&
          value !== null &&
          'feet' in value &&
          'inches' in value &&
          typeof (value as any).feet === 'number' &&
          typeof (value as any).inches === 'number'
        ) {
          const heightValue = value as { feet: number; inches: number }
          transformedData[fieldTag] = heightValue.feet * 12 + heightValue.inches
        }
        break

      case 'ssn_field':
        if (field.submit_unmasked && value) {
          transformedData[fieldTag] =
            typeof value === 'string' ? value.replace(/-/g, '') : value
        } else {
          transformedData[fieldTag] = value
        }
        break

      case 'input_table_field':
        if (Array.isArray(value)) {
          transformedData[fieldTag] = value
            .map((row) => {
              const transformedRow: Record<string, any> = {}
              Object.entries(row).forEach(([rowKey, rowValue]) => {
                const childField = field?.child_fields?.find(
                  (cf) => cf.id === rowKey
                )
                transformedRow[childField?.tag || rowKey] = rowValue
              })
              return transformedRow
            })
            .reduce((accumulator, currentValue, index) => {
              accumulator[index] = currentValue
              return accumulator
            }, {})
        }
        break

      case 'address_field':
        if (value && typeof value === 'object') {
          const addressValue = value as {
            address_line1?: string
            address_line2?: string
            zip5?: string
            address_city?: string
            address_state?: string
            county?: string
          }
          
          // Type guard to ensure we have a FormFieldDto with address properties
          const addressField = field as FormFieldDto
          
          // Transform each address component using its specific tag
          if (addressValue.address_line1 && addressField.address_line_1_tag_id) {
            transformedData[addressField.address_line_1_tag_id] = addressValue.address_line1
          }
          
          if (addressValue.address_line2 && addressField.address_line_2_tag_id) {
            transformedData[addressField.address_line_2_tag_id] = addressValue.address_line2
          }
          
          if (addressValue.zip5 && addressField.zip5_tag_id) {
            transformedData[addressField.zip5_tag_id] = addressValue.zip5
          }
          
          if (addressValue.address_city && addressField.city_tag_id) {
            transformedData[addressField.city_tag_id] = addressValue.address_city
          }
          
          if (addressValue.address_state && addressField.state_tag_id) {
            transformedData[addressField.state_tag_id] = addressValue.address_state
          }
          
          if (addressValue.county && addressField.county_tag_id) {
            transformedData[addressField.county_tag_id] = addressValue.county
          }
        }
        break

      case 'complex_phone_field':
        if (value && typeof value === 'object' && 'area_code' in value) {
          const phoneValue = value as {
            area_code: string
            central_office_code: string
            station_code: string
          }
          transformedData[fieldTag] = {
            area_code: phoneValue.area_code,
            central_office_code: phoneValue.central_office_code,
            station_code: phoneValue.station_code,
          }
        }
        break

      default:
        transformedData[fieldTag] = value
    }
  })

  return transformedData
}

// Validation utilities
export const validateField = (
  field: FormFieldDto,
  value: any,
  formData: any
) => {
  const errors = []

  // Check if field is required
  const isRequired =
    typeof field.required === 'boolean'
      ? field.required
      : field.required &&
        typeof field.required === 'object' &&
        field.required.depends_on
      ? evaluateDependency(field.required.depends_on, formData)
      : false

  if (isRequired) {
    const isEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)

    if (isEmpty) {
      errors.push(`${field.display_label} is required`)
      return errors
    }
  }

  // Advanced validation rules
  if (value && value !== '') {
    switch (field.type) {
      case 'simple_email_field':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          errors.push('Please enter a valid email address')
        }
        break
      case 'ssn_field':
        const ssnRegex = /^\d{3}-\d{2}-\d{4}$/
        if (!ssnRegex.test(value)) {
          errors.push('Please enter a valid SSN')
        }
        break
      case 'complex_phone_field':
        if (!value) {
          errors.push('Please enter a valid phone number')
        } else {
          if (
            !value.area_code ||
            value.area_code.length !== 3 ||
            !value.central_office_code ||
            value.central_office_code.length !== 3 ||
            !value.station_code ||
            value.station_code.length !== 4
          ) {
            errors.push('Please enter a valid phone number')
          }
        }
        break
      case 'text_field':
        if (field.maxlength && value.length > field.maxlength) {
          errors.push(`Maximum ${field.maxlength} characters allowed`)
        }
        break
      case 'height_field':
        if (
          value.feet < 3 ||
          value.feet > 8 ||
          value.inches < 0 ||
          value.inches > 11
        ) {
          errors.push('Please enter a valid height')
        }
        break
      case 'weight_field':
        if (field.minimum_value && value < field.minimum_value) {
          errors.push(`Minimum weight is ${field.minimum_value} lbs`)
        }
        if (field.maximum_value && value > field.maximum_value) {
          errors.push(`Maximum weight is ${field.maximum_value} lbs`)
        }
        break
      case 'string_search_field':
        if (value && value.length >= 3) {
          // Only validate if we have a search value that should trigger results
          let hasErrors = false
          field.child_fields?.forEach((child) => {
            if (
              child.required &&
              (!formData[child.id] || formData[child.id] === '')
            ) {
              hasErrors = true
            }
          })
          // Only show error if there are required child fields that are empty
          // and the search field itself has a valid search term
          if (
            hasErrors &&
            field.child_fields &&
            field.child_fields.length > 0
          ) {
            errors.push(`${field.display_label} is invalid`)
          }
        }
        break
      case 'address_field':
        if (value && typeof value === 'object') {
          // Type guard to ensure we have a FormFieldDto with address properties
          const addressField = field as FormFieldDto
          
          // Validate Address Line 1 if required
          if (addressField.address_line_1_is_required === 'yes') {
            if (!value.address_line1 || value.address_line1.trim() === '') {
              errors.push('Address Line 1 is required')
            }
          }
          
          // Validate Address Line 2 if required
          if (addressField.address_line_2_is_required === 'yes') {
            if (!value.address_line2 || value.address_line2.trim() === '') {
              errors.push('Address Line 2 is required')
            }
          }
          
          // Validate ZIP Code
          if (addressField.zip5_is_required === 'yes') {
            if (!value.zip5 || value.zip5.trim() === '') {
              errors.push('ZIP code is required')
            } else if (!/^\d{5}$/.test(value.zip5)) {
              errors.push('ZIP code is invalid')
            } else {
              // ZIP format is valid, check if lookup failed by checking if fields are missing
              // Since city/state/county are only shown after successful lookup, 
              // if ZIP is valid but no city/state/county data, then lookup failed
              const zipLookupFailed = value.zip5.length === 5 && 
                !value.address_city && !value.address_state && !value.county
              
              if (zipLookupFailed) {
                errors.push('ZIP code is invalid')
              }
            }
          }
          
          // Note: City, State, County validation is not needed here since:
          // 1. They are only rendered after successful ZIP lookup
          // 2. They are always disabled (user can't edit them)
          // 3. They are auto-populated from ZIP lookup response
          // 4. If ZIP lookup fails, the ZIP validation above will catch it
          
          // Validate whitespace requirements if specified
          if (addressField.address_line_1_has_whitespace === 'no' && value.address_line1) {
            if (/\s/.test(value.address_line1)) {
              errors.push('Address Line 1 cannot contain spaces')
            }
          }
          
          if (addressField.address_line_2_has_whitespace === 'no' && value.address_line2) {
            if (/\s/.test(value.address_line2)) {
              errors.push('Address Line 2 cannot contain spaces')
            }
          }
          
          if (addressField.zip5_has_whitespace === 'no' && value.zip5) {
            if (/\s/.test(value.zip5)) {
              errors.push('ZIP code cannot contain spaces')
            }
          }
          
          if (addressField.city_has_whitespace === 'no' && value.address_city) {
            if (/\s/.test(value.address_city)) {
              errors.push('City cannot contain spaces')
            }
          }
          
          if (addressField.state_has_whitespace === 'no' && value.address_state) {
            if (/\s/.test(value.address_state)) {
              errors.push('State cannot contain spaces')
            }
          }
          
          if (addressField.county_has_whitespace === 'no' && value.county) {
            if (/\s/.test(value.county)) {
              errors.push('County cannot contain spaces')
            }
          }
        }
        break
      case 'complex_date_picker_field':
        if (value) {
          const customValidation = getDateValidationForField(field.id)
          if (customValidation) {
            const validationResult = customValidation(value)
            if (!validationResult.isValid) {
              errors.push(validationResult.errorMessage || 'Invalid date')
            }
          }
        }
        break
      // TODO :: resolve it for child field validations
      // case 'input_table_field':
      //   if (value) {
      //     let hasErrors = false
      //     field.child_fields?.forEach((child, index) => {
      //       if (!value[index] && child.required) {
      //         hasErrors = true
      //       }
      //     })
      //     if (hasErrors) {
      //       errors.push(`Please check if fields are properly filled`)
      //     }
      //   }
      //   break
    }

    switch (field.id) {
      case 'medicare_information_claim_number':
        const medicareCardRegex =
          /^[1-9][A-Z0-9][A-Z0-9][0-9][A-Z0-9][A-Z0-9][0-9][A-Z0-9][A-Z0-9][0-9][0-9]$/
        const validChars = /^[A-Z0-9]*$/
        const excludedChars = /[SLIOZBZ]/
        const upperValue = value.toUpperCase()

        if (upperValue.length !== 11) {
          errors.push('Medicare card number must be exactly 11 characters')
        } else if (
          !validChars.test(upperValue) ||
          excludedChars.test(upperValue)
        ) {
          errors.push('Medicare card number cannot contain S, L, O, I, B, or Z')
        } else if (!medicareCardRegex.test(upperValue)) {
          errors.push(
            'Please enter a valid Medicare card number (1st character must be 1-9, positions 4,7,10,11 must be numbers 0-9)'
          )
        }
        break
    }
  }

  return errors
}

// Event processing utilities
export const processFieldEvents = (
  field: FormFieldDto,
  value: any,
  formData: any,
  updateField: any,
  allFields: FormFieldDto[]
) => {
  if (!field.trigger_events) return

  field.trigger_events.forEach((event) => {
    switch (event.event) {
      case 'age_changed':
        const dobField = allFields.find((f) =>
          f.handle_events?.some((e) => e.event === 'age_changed')
        )
        if (dobField) {
          const currentYear = new Date().getFullYear()
          const birthYear = currentYear - value
          // Convert string to proper date object for MUI DatePicker
          const estimatedDOB = dayjs(`${birthYear}-01-01`)
          updateField(dobField.id, estimatedDOB)
        }
        break
      case 'dob_changed':
        // Find age field and update it
        const ageField = allFields.find((f) =>
          f.handle_events?.some((e) => e.event === 'dob_changed')
        )
        if (ageField) {
          const today = dayjs()
          const age = today.diff(value, 'year')
          updateField(ageField.id, age)
        }
        break
      case 'producer_commission_change':
        // Find secondary commission field
        const secondaryField = allFields.find((f) =>
          f.handle_events?.some((e) => e.event === 'producer_commission_change')
        )
        if (secondaryField) {
          const remaining = 100 - value
          updateField(secondaryField.id, remaining)
        }
        break
    }
  })
}

export const getUnderwritingType = ({
  dateOfBirth,
  medicarePartBEffectiveDate,
  guaranteeIssue = false,
}: {
  dateOfBirth?: Date | null
  medicarePartBEffectiveDate?: Date | null
  guaranteeIssue?: boolean
}): number => {
  // 0 - Fully Underwritten
  // 1 - Open Enrollment
  // 2 - Guaranteed Issue

  const turned65inLastSixMonths =
    dateOfBirth && turnedSixtyFiveInLastSixMonths(dateOfBirth)
  const turned65inNextSixMonths =
    dateOfBirth && turnedSixtyFiveInNextSixMonths(dateOfBirth)
  const effectiveDatewithinLastSixMonths =
    medicarePartBEffectiveDate &&
    isWithinLastSixMonths(medicarePartBEffectiveDate)
  const effectiveDatewithinNextSixMonths =
    medicarePartBEffectiveDate &&
    isWithinNextSixMonths(medicarePartBEffectiveDate)

  if (guaranteeIssue) {
    return 2
  }

  if (
    turned65inLastSixMonths ||
    turned65inNextSixMonths ||
    effectiveDatewithinLastSixMonths ||
    effectiveDatewithinNextSixMonths
  ) {
    return 1
  }

  return 0
}

export const getInitialFormValues = (
  data: typeof customerStore,
  forSpouse: boolean,
  planType: string
) => {
  const initialValues: Record<string, any> = {
    applicant_plan: planType,
    legal_resident: true,
    apply_guaranteed_issue: false,

    // Default underwriting type
    underwriting_type: 0,

    // hidden fields
    wet_sign_application: false,
    paid_insured: true,
    initial_premium_draft_type: 'eft',
    payment_mode: 'eft',
    previous_inforce_policies: 'none',
    current_inforce_policies: 'none',
    deliver_policy_to: 'applicant',
    e_delivery: true,
    accurate_recording: true,
    interviewed_applicants: true,
    application_provided: true,
    producer_phone: parsePhoneNumber('+18008452484'),
    producer_email: 'licensing@teameip.com',
    agent_requests_split_commissions: false,
    referral: ['agent'],
  }

  if (data.msEffectiveDateFilterSelf) {
    initialValues['effective_date'] = dayjs(
      data.msEffectiveDateFilterSelf.substring(0, 10)
    )
  }

  if (!forSpouse) {
    initialValues['f_name'] = data.firstName
    initialValues['l_name'] = data.lastName

    initialValues['zip5'] = data.postalCode
    initialValues['applicant_phone'] = parsePhoneNumber(data.phone)
    initialValues['applicant_email'] = data.email
    initialValues['applicant_dob'] = dayjs(
      dayjs(data.dateOfBirth).format('YYYY-MM-DD')
    )
    initialValues['applicant_age'] = FormUtils.getAge(data.dateOfBirth)
    initialValues['gender'] = data.gender.toLowerCase() === 'male' ? 'M' : 'F'
    initialValues['hh_resident_first_name'] = data.spouseFirstName
    initialValues['hh_resident_last_name'] = data.spouseLastName
    if (data.currentMedicarePartBDateSelf) {
      initialValues['medicare_part_b_eff_date'] = dayjs(
        dayjs(data.currentMedicarePartBDateSelf).format('YYYY-MM-DD')
      )
      initialValues['medicare_part_b_coverage'] = true
      initialValues['medicare_part_b_eligibility_date'] = dayjs(
        dayjs(data.currentMedicarePartBDateSelf).format('YYYY-MM-DD')
      )
    }
    initialValues['did_turn_65_in_last_six_mo'] =
      turnedSixtyFiveInLastSixMonths(dayjs(data.dateOfBirth).toDate())
    initialValues['enroll_part_b_last_6_mo'] = isWithinLastSixMonths(
      dayjs(data.currentMedicarePartBDateSelf).toDate()
    )
  } else {
    initialValues['f_name'] = data.spouseFirstName
    initialValues['l_name'] = data.spouseLastName
    initialValues['zip5'] = data.postalCode
    initialValues['applicant_phone'] = parsePhoneNumber(data.phone)
    initialValues['applicant_email'] = data.email
    initialValues['applicant_dob'] = dayjs(
      dayjs(data.spouseDateOfBirth).format('YYYY-MM-DD')
    )
    initialValues['applicant_age'] = FormUtils.getAge(data.spouseDateOfBirth)
    initialValues['gender'] =
      data.spouseGender.toLowerCase() === 'male' ? 'M' : 'F'
    initialValues['hh_resident_first_name'] = data.firstName
    initialValues['hh_resident_last_name'] = data.lastName
    if (data.currentMedicarePartBDateSpouse) {
      initialValues['medicare_part_b_eff_date'] = dayjs(
        dayjs(data.currentMedicarePartBDateSpouse).format('YYYY-MM-DD')
      )
      initialValues['medicare_part_b_coverage'] = true
      initialValues['medicare_part_b_eligibility_date'] = dayjs(
        dayjs(data.currentMedicarePartBDateSpouse).format('YYYY-MM-DD')
      )
    }
    initialValues['did_turn_65_in_last_six_mo'] =
      turnedSixtyFiveInLastSixMonths(dayjs(data.spouseDateOfBirth).toDate())
    initialValues['enroll_part_b_last_6_mo'] = isWithinLastSixMonths(
      dayjs(data.currentMedicarePartBDateSpouse).toDate()
    )
  }

  return initialValues
}

export const formatPhoneNumber = (input: string) => {
  // Remove all non-numeric characters
  const numbers = input.replace(/\D/g, '')

  // Apply formatting based on length
  if (numbers.length === 0) return ''
  if (numbers.length <= 3) return `(${numbers}`
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
    6,
    10
  )}`
}

export const parsePhoneNumber = (formattedValue: string) => {
  const numbers = formattedValue.replace(/\D/g, '')

  // Extract the last 10 digits for US phone number format
  const cleanNumbers = numbers.length > 10 ? numbers.slice(-10) : numbers

  return {
    area_code: cleanNumbers.slice(0, 3),
    central_office_code: cleanNumbers.slice(3, 6),
    station_code: cleanNumbers.slice(6, 10),
  }
}

// Returns the applicant's email/phone from the e-app form data, formatted for
// the lite-session upgrade call, only when BOTH are present and valid.
// Used to trigger the session->customer/lead upgrade as soon as a step
// provides both fields, rather than waiting for full e-app submission.
export const getLiteSessionUpgradeContact = (
  formData: Record<string, any>
): { email: string; phone: string; firstName?: string; lastName?: string } | null => {
  const email = formData?.['applicant_email']
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!email || !emailRegex.test(email)) return null

  const phone = formData?.['applicant_phone']
  if (
    !phone ||
    typeof phone !== 'object' ||
    !phone.area_code ||
    phone.area_code.length !== 3 ||
    !phone.central_office_code ||
    phone.central_office_code.length !== 3 ||
    !phone.station_code ||
    phone.station_code.length !== 4
  ) {
    return null
  }

  return {
    email,
    phone: `+1${phone.area_code}${phone.central_office_code}${phone.station_code}`,
    firstName: formData?.['f_name'] || undefined,
    lastName: formData?.['l_name'] || undefined,
  }
}

export const turnedSixtyFiveInLastSixMonths = (dateOfBirth: Date) => {
  const sixtyFifthBirthday = new Date(
    dateOfBirth.getFullYear() + 65,
    dateOfBirth.getMonth(),
    dateOfBirth.getDate()
  )
  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  return sixtyFifthBirthday >= sixMonthsAgo && sixtyFifthBirthday <= new Date()
}

export const isWithinLastSixMonths = (date: Date): boolean => {
  const now = new Date()
  const sixMonthsAgo = new Date(now)
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

  return date >= sixMonthsAgo && date <= now
}

export const turnedSixtyFiveInNextSixMonths = (dateOfBirth: Date) => {
  const sixtyFifthBirthday = new Date(
    dateOfBirth.getFullYear() + 65,
    dateOfBirth.getMonth(),
    dateOfBirth.getDate()
  )
  const now = new Date()
  const sixMonthsFromNow = new Date()
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6)
  return sixtyFifthBirthday > now && sixtyFifthBirthday <= sixMonthsFromNow
}

export const isWithinNextSixMonths = (date: Date): boolean => {
  const now = new Date()
  const sixMonthsFromNow = new Date(now)
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6)

  return date > now && date <= sixMonthsFromNow
}

export const getDisabledFields = () => {
  const disabledFields = [
    'paid_insured',
    'wet_sign_application',
    'initial_premium_draft_type',
    'payment_mode',
    'applicant_age',
    'did_turn_65_in_last_six_mo',
    'enroll_part_b_last_6_mo',
    'open_enrollment_note',
    'applicant_plan',
    'legal_resident',
    'apply_guaranteed_issue',
    'referral',
  ]

  return disabledFields
}

export const getDisabledSections = (disabledProducer: boolean) => {
  const disabledSections = []
  if (disabledProducer) {
    disabledSections.push('producer')
  }
  return disabledSections
}

export const getVisibleSections = (
  sections: SectionDto[],
  formData: any,
  excludedSectionIds: string[]
) => {
  const res = sections
    .filter((section) => {
      if (excludedSectionIds.includes(section.id)) return false
      if (!section.depends_on) return true
      return evaluateDependency(section.depends_on, formData)
    })
    .sort((a, b) => a.order - b.order)

  return res
}

export const getVisibleFields = (section: SectionDto, formData: any) => {
  return section.body
    .filter((field: FormFieldDto) => {
      if (getDisabledFields().includes(field.id)) {
        return false
      }
      if (!field.depends_on) return true
      return evaluateDependency(field.depends_on, formData)
    })
    .sort((a: { order: number }, b: { order: number }) => a.order - b.order)
}

export const isFieldRequired = (field: FormFieldDto, formData: any) => {
  if (typeof field.required === 'boolean') {
    return field.required
  }
  if (
    field.required &&
    typeof field.required === 'object' &&
    field.required.depends_on
  ) {
    return evaluateDependency(field.required.depends_on, formData)
  }
  return false
}

export const getDateValidationForField = (
  fieldId: string
): DateValidationFunction | undefined => {
  const validationMap: Record<string, DateValidationFunction> = {
    effective_date: createFutureOrCurrentDateValidation(
      'Medicare Supplement effective date must be in the future'
    ),
    existing_coverage_medicare_plan_start_date: createPastDateValidation(),
    other_health_ins_carrier_eff_date: createPastDateValidation(),
  }

  return validationMap[fieldId]
}
